// Customer presentation only. Raw identifiers remain unchanged in stored telemetry.
const aliases: Record<string, string> = {
  'network.rx_bps': 'network.receive.bits_per_second',
  'network.tx_bps': 'network.transmit.bits_per_second',
  'network.rx_total_bytes': 'network.receive.total.bytes',
  'network.tx_total_bytes': 'network.transmit.total.bytes',
  'disk.read_bps': 'disk.read.bytes_per_second',
  'disk.write_bps': 'disk.write.bytes_per_second',
};

const labels: Record<string, string> = {
  'cpu.percent': 'CPU Utilization',
  'memory.percent': 'Memory Utilization',
  'storage.percent': 'Storage Utilization',
  'disk.percent': 'Disk Utilization',
  'virtual_disk.capacity.bytes': 'Virtual Disk Capacity',
  'virtual_disk.allocated.bytes': 'Virtual Disk Allocation',
  'guest.filesystem.percent': 'Guest Filesystem Usage',
  'guest.filesystem.utilization_percent': 'Filesystem Utilization',
  'network.receive.bits_per_second': 'Network In',
  'network.transmit.bits_per_second': 'Network Out',
  'network.receive.total.bytes': 'Network Receive Total Bytes',
  'network.transmit.total.bytes': 'Network Transmit Total Bytes',
  'disk.read.bytes_per_second': 'Disk Read Throughput',
  'disk.write.bytes_per_second': 'Disk Write Throughput',
  'disk.read.total.bytes': 'Disk Read Total Bytes',
  'disk.write.total.bytes': 'Disk Write Total Bytes',
  'disk.iops': 'Disk IOPS',
  'disk.latency.ms': 'Disk Latency',
  'uptime.seconds': 'Uptime',
  'io.wait.percent': 'I/O Wait',
  'swap.percent': 'Swap Utilization',
  'load.average': 'System Load',
  'temperature.celsius': 'Temperature',
  'humidity.percent': 'Humidity',
  'network.error.percent': 'Network Error Rate',
  'interface.error.percent': 'Interface Error Rate',
  'packet.error.percent': 'Packet Error Rate',
};

export function canonicalMetricId(metric: string): string {
  const derived = /\.(rate_per_hour|trend|deviation|forecast)$/.exec(metric);
  if (derived) return canonicalMetricId(metric.slice(0, -derived[0].length)) + derived[0];
  return aliases[metric] ?? metric;
}

export function customerMetricLabel(metric: string): string {
  metric = canonicalMetricId(metric);
  const derived = /\.(rate_per_hour|trend|deviation|forecast)$/.exec(metric);
  if (derived) {
    const base = customerMetricLabel(metric.slice(0, -derived[0].length));
    const suffix = derived[1] === 'rate_per_hour' ? 'rate of change' : derived[1];
    return `${base.replace(/\b(Utilization|Throughput|Receive|Transmit|Total|Bytes|Capacity|Allocation|Filesystem|Usage|Read|Write|Free|Used)\b/g, (word) => word.toLowerCase())} ${suffix}`;
  }
  const filesystem =
    /^guest\.filesystem\.(capacity_bytes|used_bytes|free_bytes|utilization_percent)\.([a-z0-9_-]+)$/i.exec(
      metric,
    );
  if (filesystem) {
    const measurement: Record<string, string> = {
      capacity_bytes: 'Capacity',
      used_bytes: 'Used',
      free_bytes: 'Free',
      utilization_percent: 'Utilization',
    };
    return `Guest Filesystem ${measurement[filesystem[1]!]} (${filesystem[2]!.replaceAll('_', ' ').toUpperCase()})`;
  }
  if (metric.startsWith('temperature.celsius.')) return customerTemperatureLabel(metric);
  // Unknown keys must not be prettified into an invented metric meaning.
  return labels[metric] ?? 'Additional metric';
}

export function customerTemperatureLabel(metric: string): string {
  const sensor = /^temperature\.celsius\.(?:\d+_)?([a-z0-9 _-]+)$/i.exec(metric)?.[1];
  return sensor
    ? sensor.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Temperature';
}

export function customerFinding<T extends Record<string, unknown>>(
  finding: T,
  entityName?: unknown,
) {
  const displayEntityName = String(
    entityName ?? finding.assetName ?? finding.displayName ?? '',
  ).trim();
  const displayMetricName = customerMetricLabel(
    String(finding.metric ?? finding.metric_name ?? ''),
  );
  const displayFindingLabel = `${displayEntityName ? `${displayEntityName} — ` : ''}${displayMetricName}`;
  const observed = finding.observedValue ?? finding.observed_value;
  const lower = finding.expectedLower ?? finding.expected_lower;
  const upper = finding.expectedUpper ?? finding.expected_upper;
  const finite = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);
  const detail = finite(observed)
    ? `value ${observed.toFixed(2)}${finite(lower) && finite(upper) ? `; expected ${lower.toFixed(2)} to ${upper.toFixed(2)}` : ''}.`
    : 'behavioral change observed.';
  const summary =
    finding.state === 'recovered'
      ? `${displayFindingLabel} returned to its expected behavioral range.`
      : `${displayFindingLabel} — ${detail}`;
  return { ...finding, displayEntityName, displayMetricName, displayFindingLabel, summary };
}
